import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
	fitnessProfileDateFieldShellClassName,
	fitnessProfileDateInputClassName,
	fitnessProfileFormErrorClassName,
	fitnessProfileModalBackdropClassName
} from './shared/fitnessProfileReadingStyles';
import {
	accountOwnerProfileIdentity,
	buildUpdateFitnessProfilePayload,
	computeAgeFromDateOfBirth,
	DIET_PATTERN_OPTIONS,
	EXERCISE_FREQUENCY_OPTIONS,
	EXERCISE_INTENSITY_OPTIONS,
	profileToEditFormData,
	validateFitnessProfileEditForm
} from '../../constants/fitnessProfileQuestionnaire';
import { useAuth } from '../../contexts/AuthContext';
import HeightInput from './HeightInput';

const inputClassName =
	'block w-full rounded-xl border border-gray-300 dark:border-dark-border bg-white dark:bg-dark-bg px-3 py-2 text-sm text-gray-900 dark:text-dark-text outline-none transition placeholder:text-gray-400 dark:placeholder:text-dark-text-secondary focus:border-blue-500 focus:ring-2 focus:ring-blue-500 dark:focus:border-blue-400 dark:focus:ring-blue-400';

const readOnlyInputClassName =
	'block w-full rounded-xl border border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-card px-3 py-2 text-sm text-gray-900 dark:text-dark-text outline-none transition cursor-default focus:ring-0';

const selectClassName =
	'block w-full rounded-xl border border-gray-300 dark:border-dark-border bg-white dark:bg-dark-bg px-3 py-2 text-sm text-gray-900 dark:text-dark-text outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500 dark:focus:border-blue-400 dark:focus:ring-blue-400';

const sectionCardClassName =
	'rounded-xl border border-gray-200 bg-gray-50 p-4 space-y-4 dark:border-dark-border dark:bg-dark-bg';

function Field({ id, label, required = false, hint, error, children }) {
	return (
		<label htmlFor={id} className="block text-sm">
			<span className="font-medium text-gray-700 dark:text-dark-text">
				{label}
				{required && <span className="text-red-600 dark:text-red-400"> *</span>}
			</span>
			<div className="mt-1.5">{children}</div>
			{hint && (
				<p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
					{hint}
				</p>
			)}
			{error && (
				<p
					id={`${id}-error`}
					className="mt-1.5 text-xs font-medium text-red-600 dark:text-red-400"
				>
					{error}
				</p>
			)}
		</label>
	);
}

function FitnessProfileEditModal({
	isOpen,
	profile,
	onClose,
	onSave,
	saving,
	error,
	user
}) {
	const modalRef = useRef(null);
	const [
		formData,
		setFormData
	] = useState(() => profileToEditFormData(profile));
	const [
		fieldErrors,
		setFieldErrors
	] = useState({});

	useEffect(
		() => {
			if (isOpen && profile) {
				setFormData(profileToEditFormData(profile));
				setFieldErrors({});
			}
		},
		[
			isOpen,
			profile
		]
	);

	useEffect(
		() => {
			function handleClickOutside(event) {
				if (modalRef.current && !modalRef.current.contains(event.target)) {
					onClose();
				}
			}

			if (isOpen) {
				document.addEventListener('mousedown', handleClickOutside);
			} else {
				document.removeEventListener('mousedown', handleClickOutside);
			}

			return () =>
				document.removeEventListener('mousedown', handleClickOutside);
		},
		[
			isOpen,
			onClose
		]
	);

	const computedAge = useMemo(
		() => computeAgeFromDateOfBirth(formData.dateOfBirth),
		[
			formData.dateOfBirth
		]
	);

	if (!isOpen || !profile) return null;

	const isPrimaryProfile = Boolean(profile.is_primary);
	const accountIdentity = isPrimaryProfile ? accountOwnerProfileIdentity(user) : null;
	const displayName = isPrimaryProfile ? accountIdentity.name : formData.name;
	const displayEmail = isPrimaryProfile ? accountIdentity.email : formData.email;

	function onInputChange(event) {
		const { name, value } = event.target;
		setFormData((previous) => ({ ...previous, [name]: value }));
	}

	function handleSubmit(event) {
		event.preventDefault();
		const errors = validateFitnessProfileEditForm(formData, { isPrimary: isPrimaryProfile });
		setFieldErrors(errors);
		if (Object.keys(errors).length > 0) return;
		onSave(buildUpdateFitnessProfilePayload(formData, { isPrimary: isPrimaryProfile }));
	}

	return (
		<div className={fitnessProfileModalBackdropClassName}>
			<div
				ref={modalRef}
				className="relative flex w-full max-w-2xl flex-col overflow-hidden rounded-lg border border-gray-200 bg-white text-gray-800 shadow-xl dark:border-dark-border dark:bg-dark-card dark:text-dark-text"
				style={{ maxHeight: '90vh' }}
				role="dialog"
				aria-modal="true"
				aria-labelledby="fitness-profile-edit-modal-title"
			>
				<div className="relative shrink-0 border-b border-gray-200 px-8 pb-4 pt-6 pr-14 dark:border-dark-border">
					<h2
						id="fitness-profile-edit-modal-title"
						className="text-2xl font-bold text-gray-900 dark:text-dark-text"
					>
						Edit profile
					</h2>
					<button
						type="button"
						className="absolute right-2 top-4 rounded-full p-2 transition duration-300 hover:bg-gray-200 dark:hover:bg-gray-700"
						onClick={onClose}
						aria-label="Close"
					>
						<svg
							className="h-6 w-6 text-gray-500 dark:text-gray-300"
							fill="none"
							viewBox="0 0 24 24"
							stroke="currentColor"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth="2"
								d="M6 18L18 6M6 6l12 12"
							/>
						</svg>
					</button>
				</div>

				<form
					onSubmit={handleSubmit}
					className="flex min-h-0 flex-1 flex-col overflow-hidden"
				>
					<div className="min-h-0 flex-1 overflow-y-auto px-8 py-4">
						{error && (
							<p
								className={`mb-4 ${fitnessProfileFormErrorClassName}`}
								role="alert"
							>
								{error}
							</p>
						)}

						<div className="space-y-4">
							<div className={sectionCardClassName}>
								<p className="text-sm font-semibold text-gray-900 dark:text-dark-text">
									Basic profile
								</p>
								<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
									<Field
										id="edit-name"
										label="Name"
										required
										error={fieldErrors.name}
										hint={
											isPrimaryProfile
												? 'Uses your account first name. Update your user profile to change.'
												: undefined
										}
									>
										<input
											id="edit-name"
											name="name"
											value={displayName}
											onChange={onInputChange}
											readOnly={isPrimaryProfile}
											className={isPrimaryProfile ? readOnlyInputClassName : inputClassName}
											autoComplete="name"
										/>
									</Field>
									<Field
										id="edit-email"
										label="Email"
										required
										error={fieldErrors.email}
										hint={
											isPrimaryProfile
												? 'Inherited from your account. Used for challenge and weigh-in reminders.'
												: 'For challenge and weigh-in reminders. Must be unique across Fantasy Fitness and cannot match another account login.'
										}
									>
										<input
											id="edit-email"
											type="email"
											name="email"
											value={displayEmail}
											onChange={onInputChange}
											readOnly={isPrimaryProfile}
											className={isPrimaryProfile ? readOnlyInputClassName : inputClassName}
											autoComplete="email"
										/>
									</Field>
									<Field
										id="edit-dateOfBirth"
										label="Date of birth"
										required
										hint={
											computedAge != null ? `Age ${computedAge}` : undefined
										}
										error={fieldErrors.dateOfBirth}
									>
										<div className="min-w-0 w-full">
											<div
												className={fitnessProfileDateFieldShellClassName(
													Boolean(fieldErrors.dateOfBirth)
												)}
											>
												<input
													id="edit-dateOfBirth"
													type="date"
													name="dateOfBirth"
													value={formData.dateOfBirth}
													onChange={onInputChange}
													className={fitnessProfileDateInputClassName}
												/>
											</div>
										</div>
									</Field>
									<Field
										id="edit-height"
										label="Height"
										required
										hint="Use the dropdowns — no need to type feet or inch marks."
										error={fieldErrors.height}
									>
										<HeightInput
											id="edit-height"
											value={formData.height}
											onChange={(display) =>
												setFormData((prev) => ({ ...prev, height: display }))}
											inputClassName={inputClassName}
											hasError={Boolean(fieldErrors.height)}
										/>
									</Field>
								</div>

								<label className="mt-4 flex cursor-pointer items-start gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3 dark:border-dark-border dark:bg-dark-card">
									<input
										type="checkbox"
										className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-dark-border"
										checked={formData.wednesdayWeighInRemindersEnabled}
										onChange={(event) => {
											const enabled = event.target.checked;
											setFormData((previous) => ({
												...previous,
												wednesdayWeighInRemindersEnabled: enabled
											}));
										}}
									/>
									<span className="text-sm">
										<span className="font-medium text-gray-900 dark:text-dark-text">
											Wednesday weigh-in reminders
										</span>
										<span className="mt-0.5 block text-gray-500 dark:text-gray-400">
											We'll email this address on Wednesdays when you
											haven't logged weight yet this week.
										</span>
									</span>
								</label>
							</div>

							<div className={sectionCardClassName}>
								<p className="text-sm font-semibold text-gray-900 dark:text-dark-text">
									Current habits
								</p>
								<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
									<Field
										id="edit-dietPattern"
										label="Diet pattern"
										required
										error={fieldErrors.dietPattern}
									>
										<select
											id="edit-dietPattern"
											name="dietPattern"
											value={formData.dietPattern}
											onChange={onInputChange}
											className={selectClassName}
										>
											<option value="">Select an option</option>
											{DIET_PATTERN_OPTIONS.map((option) => (
												<option key={option.value} value={option.value}>
													{option.label}
												</option>
											))}
										</select>
									</Field>
									<Field
										id="edit-exerciseFrequency"
										label="Exercise frequency"
										required
										error={fieldErrors.exerciseFrequency}
									>
										<select
											id="edit-exerciseFrequency"
											name="exerciseFrequency"
											value={formData.exerciseFrequency}
											onChange={onInputChange}
											className={selectClassName}
										>
											<option value="">Select an option</option>
											{EXERCISE_FREQUENCY_OPTIONS.map((option) => (
												<option key={option.value} value={option.value}>
													{option.label}
												</option>
											))}
										</select>
									</Field>
									<Field
										id="edit-exerciseIntensity"
										label="Exercise intensity"
										required
										error={fieldErrors.exerciseIntensity}
									>
										<select
											id="edit-exerciseIntensity"
											name="exerciseIntensity"
											value={formData.exerciseIntensity}
											onChange={onInputChange}
											className={selectClassName}
										>
											<option value="">Select an option</option>
											{EXERCISE_INTENSITY_OPTIONS.map((option) => (
												<option key={option.value} value={option.value}>
													{option.label}
												</option>
											))}
										</select>
									</Field>
									<Field
										id="edit-sleepHours"
										label="Average sleep (hours)"
										required
										error={fieldErrors.sleepHours}
									>
										<input
											id="edit-sleepHours"
											type="number"
											name="sleepHours"
											value={formData.sleepHours}
											onChange={onInputChange}
											min="1"
											max="14"
											step="0.1"
											className={inputClassName}
										/>
									</Field>
									<div className="sm:col-span-2">
										<Field
											id="edit-dietNotes"
											label="Diet notes (optional)"
											hint="Meal timing, cravings, or dietary constraints."
										>
											<textarea
												id="edit-dietNotes"
												name="dietNotes"
												value={formData.dietNotes}
												onChange={onInputChange}
												rows={2}
												className={inputClassName}
											/>
										</Field>
									</div>
									<div className="sm:col-span-2">
										<Field
											id="edit-habitNotes"
											label="Training notes (optional)"
											hint="Injuries, preferred split, schedule constraints."
										>
											<textarea
												id="edit-habitNotes"
												name="habitNotes"
												value={formData.habitNotes}
												onChange={onInputChange}
												rows={2}
												className={inputClassName}
											/>
										</Field>
									</div>
								</div>
							</div>

							<div className={sectionCardClassName}>
								<p className="text-sm font-semibold text-gray-900 dark:text-dark-text">
									Equipment access
								</p>
								<Field
									id="edit-equipmentAccess"
									label="What equipment do you have?"
									hint="List gym access, free weights, resistance bands, cardio machines, or home setup details."
								>
									<textarea
										id="edit-equipmentAccess"
										name="equipmentAccess"
										value={formData.equipmentAccess}
										onChange={onInputChange}
										rows={4}
										placeholder="Planet Fitness membership, dumbbells at home, resistance bands, treadmill."
										className={inputClassName}
									/>
								</Field>
							</div>

						</div>
					</div>

					<div className="flex shrink-0 flex-wrap justify-end gap-2 border-t border-gray-200 px-8 py-4 dark:border-dark-border">
						<button
							type="button"
							onClick={onClose}
							disabled={saving}
							className="rounded-lg bg-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-300 disabled:opacity-60 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
						>
							Cancel
						</button>
						<button
							type="submit"
							disabled={saving}
							className="rounded-lg bg-blue-500 px-4 py-2 text-sm font-medium text-white hover:bg-blue-600 disabled:opacity-60"
						>
							{saving ? 'Saving…' : 'Save changes'}
						</button>
					</div>
				</form>
			</div>
		</div>
	);
}

export default function FitnessProfileEditModalWithAuth(props) {
	const { user } = useAuth();
	return <FitnessProfileEditModal {...props} user={user} />;
}
